import { useMemo } from 'react';

import supportedGoogleFronts from '../assets/supportedGoogleFonts';
import { FontVariant, WebFontResource } from '../types';

interface GoogleFont {
  family: string;
  variants: FontVariant[];
}

const mapWeight = (variant: FontVariant) => {
  if (variant === 'normal') {
    return '0,400';
  }
  if (variant === 'italic') {
    return '1,400';
  }
  return (variant.includes('ital') ? '1,' : '0,') + variant.replace(/\D+/g, '');
};

/**
 * Googles css2 endpoint is quite opinionated but very functional
 *
 * Docs are here: https://developers.google.com/fonts/docs/css2
 *
 * Essentially, we define each variant as a tuple of {italic,weight}, these must be sorted alphabetically
 * presumably to improve caching between different websites consuming the API
 *
 * As a future optimisation to this, we could import only the fonts the user's styleguide is using, this would
 * necessitate a further refactor to generate URLs at both view time and styleguide save time
 */
export const getGoogleFontUrl = (fontFamily: string, variants: FontVariant[]) => {
  const weights = variants
    .map(mapWeight)
    .filter((variant) => variant !== '0,' && variant !== '1,')
    .sort((a, b) => a.localeCompare(b));

  const uniqueWeights = [...new Set(weights)];

  return `https://fonts.googleapis.com/css2?display=block&family=${fontFamily.replace(
    / /g,
    '+',
  )}:ital,wght@${uniqueWeights.join(';')}`;
};

const mapGoogleFont = ({ family, variants }: GoogleFont): WebFontResource => ({
  family,
  variants,
  url: getGoogleFontUrl(family, variants),
  type: 'google',
});

export const useGoogleFonts = () => {
  return useMemo(() => supportedGoogleFronts.map(mapGoogleFont), []);
};
